import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@1trading/react';
import { useId, useState } from 'react';
import { closeOnly } from '@/components/dialogs/close-only';
import { FieldError } from '@/components/ui/field-error';
import { omit } from '@/utils/object';
import { oneOffEndBounds, scheduleEndBounds, scheduleStartBounds } from './date-bounds';
import { DOWNTIME_DAY_LABELS, findDowntimeRuleProblems, hasDowntimeStarted, moveOneOffStart } from './mm-program-editing';
import { CheckboxField, DateField, DateTimeField, TextField, TimeField } from './mm-program-fields';
import { DOWNTIME_DAYS, type DowntimeRuleInput } from './mm-program-schema';

interface DowntimeRuleDialogProps {
  rule: DowntimeRuleInput;
  isNew: boolean;
  onClose: () => void;
  onSave: (rule: DowntimeRuleInput) => void;
}

interface DowntimeRuleFieldsProps {
  draft: DowntimeRuleInput;
  onChange: (draft: DowntimeRuleInput) => void;
  /** The first problem on each field, from `findDowntimeRuleProblems` */
  problems: Partial<Record<string, string>>;
}

/** A rule's own fields: reason, when it applies, and whether it is enabled. */
export const DowntimeRuleFields = ({ draft, onChange: setDraft, problems }: DowntimeRuleFieldsProps) => {
  const daysLegendId = useId();
  // A blank bound is absent from the rule, so the rule applies open-ended on that side.
  const setDay = (field: 'start_date' | 'end_date', day: string) => {
    if (draft.type === 'recurring') setDraft(day ? { ...draft, [field]: day } : omit(draft, field));
  };

  return (
    <div className="flex flex-col gap-4">
      <TextField error={problems.reason} label="Reason" onChange={(reason) => setDraft({ ...draft, reason })} placeholder="Weekly maintenance" value={draft.reason} />
      {draft.type === 'recurring' ? (
        <>
          <fieldset aria-labelledby={daysLegendId} className="flex flex-col gap-1.5">
            <legend className="text-sm" id={daysLegendId}>
              Days
            </legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DOWNTIME_DAYS.map((day) => (
                <CheckboxField
                  checked={draft.days.includes(day)}
                  key={day}
                  label={DOWNTIME_DAY_LABELS[day]}
                  onChange={(checked) => setDraft({ ...draft, days: DOWNTIME_DAYS.filter((other) => (other === day ? checked : draft.days.includes(other))) })}
                />
              ))}
            </div>
            <FieldError message={problems.days} />
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <TimeField error={problems.start_time} label="Start" onChange={(start_time) => setDraft({ ...draft, start_time })} value={draft.start_time} />
            <TimeField error={problems.end_time} label="End" onChange={(end_time) => setDraft({ ...draft, end_time })} value={draft.end_time} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DateField
              asDay
              clearLabel="Clear: leave open"
              error={problems.start_date}
              label="From (inclusive, UTC)"
              max={scheduleStartBounds({ end_date: draft.end_date ?? '' }).max}
              onChange={(day) => setDay('start_date', day)}
              placeholder="Open, no first day"
              value={draft.start_date ?? ''}
            />
            <DateField
              asDay
              clearLabel="Clear: leave open"
              error={problems.end_date}
              label="Until (exclusive, UTC)"
              min={scheduleEndBounds({ start_date: draft.start_date ?? '' }).min}
              onChange={(day) => setDay('end_date', day)}
              placeholder="Open, no end date"
              value={draft.end_date ?? ''}
            />
          </div>
          {hasDowntimeStarted(draft) ? (
            <p className="text-xs text-muted-foreground">
              {draft.start_date ? 'This rule starts in the past.' : 'Without a first day, this rule also covers the past.'} Runs it covers that are already scored apply it once
              they are rescored from Jobs.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <DateTimeField error={problems.start} label="Start" onChange={(start) => setDraft(moveOneOffStart(draft, start))} value={draft.start} />
          <DateTimeField error={problems.end} label="End" min={oneOffEndBounds(draft.start).min} onChange={(end) => setDraft({ ...draft, end })} value={draft.end} />
          {hasDowntimeStarted(draft) ? (
            <p className="text-xs text-muted-foreground">This window starts in the past. Runs it covers that are already scored apply it once they are rescored from Jobs.</p>
          ) : null}
        </>
      )}
      <CheckboxField checked={draft.enabled} label="Enabled" onChange={(enabled) => setDraft({ ...draft, enabled })} />
    </div>
  );
};
DowntimeRuleFields.displayName = 'DowntimeRuleFields';

export const DowntimeRuleDialog = ({ rule, isNew, onClose, onSave }: DowntimeRuleDialogProps) => {
  const [draft, setDraft] = useState(rule);
  // Problems show once a save is attempted, so a blank new rule does not open full of errors.
  const [hasAttempted, setHasAttempted] = useState(false);
  const problems = hasAttempted ? findDowntimeRuleProblems(draft) : {};
  const kind = draft.type === 'recurring' ? 'recurring' : 'one-off';

  const save = () => {
    setHasAttempted(true);
    if (!Object.keys(findDowntimeRuleProblems(draft)).length) onSave(draft);
  };

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open>
      <DialogContent aria-describedby={undefined} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? `Add ${kind} downtime` : `Edit ${kind} downtime`}</DialogTitle>
        </DialogHeader>
        <DowntimeRuleFields draft={draft} onChange={setDraft} problems={problems} />
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button onClick={save} type="button">
            {isNew ? 'Add rule' : 'Update rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
DowntimeRuleDialog.displayName = 'DowntimeRuleDialog';
